import React, { useState, useEffect, useCallback } from 'react';
import { Project, KeyGoal } from '../../types';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { MonthYearPicker } from '../common/MonthYearPicker';
import { compareMonthYear } from '../../utils/date-formatter';

interface ProjectFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (project: Omit<Project, 'projectId'> | Project) => void;
  onDelete?: (projectId: string) => void;
  project: Project | null;
  keyGoals?: KeyGoal[];
  presidioAreas?: string[];
  projects?: Project[]; // Add projects for dependency dropdown
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  project,
  keyGoals = [],
  presidioAreas = [],
  projects = []
}) => {
  const [formData, setFormData] = useState<Omit<Project, 'projectId'> | Project>({
    projectName: '',
    overview: '',
    presidioControlled: 'No',
    modernizationPlanProject: 'No',
    startDate: '', // Allow any month selection
    endDate: '',
    dependsOnProjectId: '',
    goals: [],
    presidioAreas: []
  });
  
  const [charCount, setCharCount] = useState(0);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isCloseConfirmOpen, setIsCloseConfirmOpen] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState<KeyGoal[]>([]);
  const [selectedPresidioAreas, setSelectedPresidioAreas] = useState<string[]>([]);
  const [goalsDropdownOpen, setGoalsDropdownOpen] = useState(false);
  const [areasDropdownOpen, setAreasDropdownOpen] = useState(false);
  const [goalsSearchTerm, setGoalsSearchTerm] = useState('');
  const [areasSearchTerm, setAreasSearchTerm] = useState('');

  // Max end date = this project's start date + 18 months (6 quarters). Single source of truth for end-date rule.
  const getMaxEndDateFromStart = useCallback((startMonth: string): string | undefined => {
    if (!startMonth || !/^\d{4}-\d{2}$/.test(startMonth)) return undefined;
    const [year, month] = startMonth.split('-').map(Number);
    let newYear = year;
    let newMonth = month + 18;
    while (newMonth > 12) {
      newMonth -= 12;
      newYear += 1;
    }
    return `${newYear}-${newMonth.toString().padStart(2, '0')}`;
  }, []);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!project) {
      // For new projects, check if any field has been filled
      return formData.projectName.trim() !== '' || 
             formData.overview.trim() !== '' ||
             formData.presidioControlled !== 'No' ||
             formData.modernizationPlanProject !== 'No' ||
             selectedGoals.length > 0 ||
             selectedPresidioAreas.length > 0 ||
             formData.endDate !== '' ||
             formData.dependsOnProjectId !== '';
    } else {
      // For existing projects, check if any field has changed
      return formData.projectName !== project.projectName ||
             formData.overview !== project.overview ||
             formData.presidioControlled !== project.presidioControlled ||
             formData.modernizationPlanProject !== project.modernizationPlanProject ||
             formData.startDate !== project.startDate ||
             formData.endDate !== project.endDate ||
             formData.dependsOnProjectId !== project.dependsOnProjectId ||
             JSON.stringify(selectedGoals) !== JSON.stringify(project.goals || []) ||
             JSON.stringify(selectedPresidioAreas) !== JSON.stringify(project.presidioAreas || []);
    }
  }, [formData, project, selectedGoals, selectedPresidioAreas]);

  const handleCloseAttempt = useCallback(() => {
    if (hasUnsavedChanges()) {
      setIsCloseConfirmOpen(true);
    } else {
      onClose();
    }
  }, [hasUnsavedChanges, onClose]);

  const handleConfirmClose = () => {
    setIsCloseConfirmOpen(false);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseAttempt();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, formData, selectedGoals, selectedPresidioAreas, project, handleCloseAttempt]);

  useEffect(() => {
    
    if (project) {
      setFormData({
        ...project
      });
      setCharCount(project.overview.length);
      
      // Initialize selected goals and presidio areas from project data
      if (project.goals) {
        setSelectedGoals(project.goals);
      } else {
        setSelectedGoals([]);
      }
      
      if (project.presidioAreas) {
        setSelectedPresidioAreas(project.presidioAreas);
      } else {
        setSelectedPresidioAreas([]);
      }
    } else {
      setFormData({
        projectName: '',
        overview: '',
        presidioControlled: 'No',
        modernizationPlanProject: 'No',
        startDate: '', // Allow any month selection
        endDate: '',
        dependsOnProjectId: '',
        goals: [],
        presidioAreas: []
      });
      setCharCount(0);
      setSelectedGoals([]);
      setSelectedPresidioAreas([]);
    }
    setFormErrors({});
  }, [project, isOpen, keyGoals, presidioAreas]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'overview') {
      if (value.length <= 2000) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Separate handler for date field changes
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when field is edited
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Real-time validation for end month
    if (field === 'endDate' && value) {
      const errors: string[] = [];
      
      // Check if end month is before start month
      if (formData.startDate) {
        if (compareMonthYear(value, formData.startDate) < 0) {
          errors.push('End month cannot be before start month');
        }
      }
      
      // Check if end month exceeds 6 quarters (18 months) from this project's start
      const maxEndDate = formData.startDate ? getMaxEndDateFromStart(formData.startDate) : undefined;
      if (maxEndDate) {
        if (compareMonthYear(value, maxEndDate) > 0) {
          errors.push(`End month cannot be more than 6 quarters (18 months) from the project start month`);
        }
      }
      
      if (errors.length > 0) {
        setFormErrors(prev => ({ ...prev, endDate: errors.join('. ') }));
      } else {
        setFormErrors(prev => ({ ...prev, endDate: '' }));
      }
    }
    
    // Real-time validation for start month (re-validate end month when start month changes)
    if (field === 'startDate' && formData.endDate) {
      const errors: string[] = [];
      
      // Check if end month is before start month
      if (compareMonthYear(formData.endDate, value) < 0) {
        errors.push('End month cannot be before start month');
      }
      
      // Re-check if end month exceeds 6 quarters from this project's start using the new start month value
      const maxEndDate = value ? getMaxEndDateFromStart(value) : undefined;
      if (maxEndDate) {
        if (compareMonthYear(formData.endDate, maxEndDate) > 0) {
          errors.push(`End month cannot be more than 6 quarters (18 months) from the project start month`);
        }
      }
      
      if (errors.length > 0) {
        setFormErrors(prev => ({ ...prev, endDate: errors.join('. ') }));
      } else {
        setFormErrors(prev => ({ ...prev, endDate: '' }));
      }
    }
  };
  
  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.projectName.trim()) {
      errors.name = 'Project name is required';
    }
    
    if (!formData.startDate) {
      errors.startDate = 'Start month is required';
    }
    
    if (selectedGoals.length === 0) {
      errors.goals = 'At least one key goal is required';
    }
    
    if (selectedPresidioAreas.length === 0) {
      errors.presidioAreas = 'At least one presidio area is required';
    }
    
    if (!formData.overview.trim()) {
      errors.overview = 'Project overview is required';
    }
    
    // Validate that end month is not before start month
    if (formData.startDate && formData.endDate) {
      if (compareMonthYear(formData.endDate, formData.startDate) < 0) {
        errors.endDate = 'End month cannot be before start month';
      }
    }
    
    // Validate that end month doesn't exceed 6 quarters (18 months) from this project's start
    if (formData.endDate && formData.startDate) {
      const maxEndDate = getMaxEndDateFromStart(formData.startDate);
      if (maxEndDate && compareMonthYear(formData.endDate, maxEndDate) > 0) {
        errors.endDate = `End month cannot be more than 6 quarters (18 months) from the project start month`;
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm() && !isSubmitting) {
      setIsSubmitting(true);
      
      // Call onSubmit and handle the promise
      Promise.resolve(onSubmit(formData))
        .finally(() => {
          // Reset loading state when API call completes
          setIsSubmitting(false);
        });
    }
  };

  const handleDelete = () => {
    if (project && onDelete) {
      onDelete(project.projectId as string);
      setIsDeleteConfirmOpen(false);
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-end min-h-screen">
        {/* Backdrop overlay */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-40 transition-opacity" 
          onClick={handleCloseAttempt}
          aria-hidden="true"
        ></div>
        
        {/* Side panel */}
        <div className="relative bg-white h-screen w-[500px] shadow-xl overflow-y-auto">
          <div className="px-6 py-4">
            {/* Header with close button */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold italic text-[#0F2A4F]">
                {project ? 'Edit Project' : 'Add New Project'}
              </h2>
              <button 
                onClick={handleCloseAttempt}
                className="rounded-full bg-gray-200 p-1 hover:bg-gray-300 focus:outline-none"
                type="button"
              >
                <svg className="h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-2">
                {/* Project Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Project Name
                  </label>
                  <input
                    type="text"
                    id="projectName"
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleChange}
                    className={`w-full border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                  )}
                </div>
                
                {/* Start Month and End Month in the same row */}
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <MonthYearPicker
                    value={formData.startDate || ''}
                    onChange={(value) => handleDateChange('startDate', value)}
                    label="Start Month *"
                    placeholder="Select start month"
                    error={formErrors.startDate}
                  />
                  
                  <MonthYearPicker
                    value={formData.endDate || ''}
                    onChange={(value) => handleDateChange('endDate', value)}
                    label="End Month (Optional)"
                    placeholder="Select end month"
                    error={formErrors.endDate}
                    minDate={formData.startDate || undefined}
                    maxDate={formData.startDate ? getMaxEndDateFromStart(formData.startDate) : undefined}
                  />
                </div>
                

                
                {/* Presidio Controlled and Modernization Plan Project in the same row */}
                <div className="mb-4 grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="presidioControlled" className="block text-sm font-medium text-gray-700 mb-1">
                      Presidio Controlled
                    </label>
                    <div className="relative">
                      <select
                        id="presidioControlled"
                        name="presidioControlled"
                        value={formData.presidioControlled}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border ${formErrors.presidioControlled ? 'border-red-500' : 'border-gray-300'} rounded-md appearance-none`}
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    {formErrors.presidioControlled && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.presidioControlled}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="modernizationPlanProject" className="block text-sm font-medium text-gray-700 mb-1">
                      Modernization Plan Project
                    </label>
                    <div className="relative">
                      <select
                        id="modernizationPlanProject"
                        name="modernizationPlanProject"
                        value={formData.modernizationPlanProject || 'No'}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md appearance-none"
                      >
                        <option value="No">No</option>
                        <option value="Yes">Yes</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Project Overview and Goals */}
                <div>
                  <label htmlFor="overview" className="block text-sm font-medium text-gray-700 mb-1">
                    Project Overview and Goals
                  </label>
                  <textarea
                    id="overview"
                    name="overview"
                    value={formData.overview}
                    onChange={handleChange}
                    rows={6}
                    maxLength={2000}
                    className={`w-full border ${formErrors.overview ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  ></textarea>
                  <div className="flex justify-end mt-1">
                    <span className={`text-xs ${charCount > 1800 ? 'text-orange-500' : 'text-gray-500'}`}>
                      {charCount}/2000
                    </span>
                  </div>
                  {formErrors.overview && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.overview}</p>
                  )}
                </div>
                
                {/* Key Goals Multiselect */}
                <div>
                  <label htmlFor="goals" className="block text-sm font-medium text-gray-700 mb-1">
                    Key Goals
                  </label>
                  <div className="relative">
                    {/* Selected goals display */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedGoals.map((goal, index) => (
                        <div 
                          key={index} 
                              className="bg-gray-200 text-black px-2 py-1 rounded-full flex items-center text-xs"
                        >
                          <span>{goal.goal}</span>
                          <button 
                            type="button"
                            onClick={() => {
                              const newSelectedGoals = selectedGoals.filter(g => g.goal !== goal.goal);
                              setSelectedGoals(newSelectedGoals);
                              setFormData(prev => ({ ...prev, goals: newSelectedGoals }));
                            }}
                                  className="ml-1 text-[#0F2A4F] hover:text-[#1d3455]"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Dropdown input */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="select goal"
                                              className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#0F2A4F] bg-white hover:cursor-pointer"
                        value={goalsSearchTerm}
                        onChange={(e) => setGoalsSearchTerm(e.target.value)}
                        onClick={() => setGoalsDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setGoalsDropdownOpen(false), 200)}
                      />
                      <div 
                        className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                        onClick={() => setGoalsDropdownOpen(!goalsDropdownOpen)}
                      >
                        <svg 
                          className={`h-5 w-5 text-gray-400 transition-transform ${goalsDropdownOpen ? 'transform rotate-180' : ''}`} 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Dropdown menu */}
                    <div 
                      className={`absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto ${goalsDropdownOpen ? '' : 'hidden'}`}
                    >
                      {keyGoals.length > 0 ? (
                        // Filter and sort goals based on search term
                        keyGoals
                          .filter(goal => 
                            goalsSearchTerm === '' || 
                            goal.goal.toLowerCase().includes(goalsSearchTerm.toLowerCase()) ||
                            goal.definition.toLowerCase().includes(goalsSearchTerm.toLowerCase())
                          )
                          .sort((a, b) => {
                            // If search term exists, prioritize exact matches
                            if (goalsSearchTerm) {
                              const aStartsWithTerm = a.goal.toLowerCase().startsWith(goalsSearchTerm.toLowerCase());
                              const bStartsWithTerm = b.goal.toLowerCase().startsWith(goalsSearchTerm.toLowerCase());
                              
                              if (aStartsWithTerm && !bStartsWithTerm) return -1;
                              if (!aStartsWithTerm && bStartsWithTerm) return 1;
                              
                              // Then prioritize selected items
                              const aIsSelected = selectedGoals.some(g => g.goal === a.goal);
                              const bIsSelected = selectedGoals.some(g => g.goal === b.goal);
                              
                              if (aIsSelected && !bIsSelected) return -1;
                              if (!aIsSelected && bIsSelected) return 1;
                            } else {
                              // If no search term, just prioritize selected items
                              const aIsSelected = selectedGoals.some(g => g.goal === a.goal);
                              const bIsSelected = selectedGoals.some(g => g.goal === b.goal);
                              
                              if (aIsSelected && !bIsSelected) return -1;
                              if (!aIsSelected && bIsSelected) return 1;
                            }
                            
                            return 0;
                          })
                          .map((goal, index) => (
                          <div 
                            key={index} 
                            className={`flex items-center p-2 hover:bg-gray-100 cursor-pointer ${
                              selectedGoals.some(g => g.goal === goal.goal) ? 'bg-blue-50' : ''
                            }`}
                            data-goal={goal.goal}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              if (selectedGoals.some(g => g.goal === goal.goal)) {
                                const newSelectedGoals = selectedGoals.filter(g => g.goal !== goal.goal);
                                setSelectedGoals(newSelectedGoals);
                                setFormData(prev => ({ ...prev, goals: newSelectedGoals }));
                              } else {
                                const newSelectedGoals = [...selectedGoals, goal];
                                setSelectedGoals(newSelectedGoals);
                                setFormData(prev => ({ ...prev, goals: newSelectedGoals }));
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedGoals.some(g => g.goal === goal.goal)}
                              readOnly
                              className="mr-2 pointer-events-none"
                            />
                            <span className="text-sm">{goal.goal} - {goal.definition}</span>
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500">No key goals available</div>
                      )}
                    </div>
                  </div>
                  {formErrors.goals && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.goals}</p>
                  )}
                </div>
                
                {/* Presidio Areas Multiselect */}
                <div>
                  <label htmlFor="presidio_areas" className="block text-sm font-medium text-gray-700 mb-1">
                    Presidio Areas
                  </label>
                  <div className="relative">
                    {/* Selected areas display */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      {selectedPresidioAreas.map((area, index) => (
                        <div 
                          key={index} 
                          className="bg-gray-200 text-black px-2 py-1 rounded-full flex items-center text-xs"
                        >
                          <span>{area}</span>
                          <button 
                            type="button"
                            onClick={() => {
                              const newSelectedAreas = selectedPresidioAreas.filter(a => a !== area);
                              setSelectedPresidioAreas(newSelectedAreas);
                              setFormData(prev => ({ ...prev, presidioAreas: newSelectedAreas }));
                            }}
                                  className="ml-1 text-[#0F2A4F] hover:text-[#1d3455]"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Dropdown input */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="select presidio area"
                                              className="w-full border border-gray-300 rounded-md p-2 pr-10 focus:outline-none focus:ring-[#0F2A4F] bg-white hover:cursor-pointer"
                        value={areasSearchTerm}
                        onChange={(e) => setAreasSearchTerm(e.target.value)}
                        onClick={() => setAreasDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setAreasDropdownOpen(false), 200)}
                      />
                      <div 
                        className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                        onClick={() => setAreasDropdownOpen(!areasDropdownOpen)}
                      >
                        <svg 
                          className={`h-5 w-5 text-gray-400 transition-transform ${areasDropdownOpen ? 'transform rotate-180' : ''}`} 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 20 20" 
                          fill="currentColor"
                        >
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    
                    {/* Dropdown menu */}
                    <div 
                      className={`absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto ${areasDropdownOpen ? '' : 'hidden'}`}
                    >
                      {presidioAreas.length > 0 ? (
                        // Filter and sort areas based on search term
                        presidioAreas
                          .filter(area => 
                            areasSearchTerm === '' || 
                            area.toLowerCase().includes(areasSearchTerm.toLowerCase())
                          )
                          .sort((a, b) => {
                            // If search term exists, prioritize exact matches
                            if (areasSearchTerm) {
                              const aStartsWithTerm = a.toLowerCase().startsWith(areasSearchTerm.toLowerCase());
                              const bStartsWithTerm = b.toLowerCase().startsWith(areasSearchTerm.toLowerCase());
                              
                              if (aStartsWithTerm && !bStartsWithTerm) return -1;
                              if (!aStartsWithTerm && bStartsWithTerm) return 1;
                              
                              // Then prioritize selected items
                              const aIsSelected = selectedPresidioAreas.includes(a);
                              const bIsSelected = selectedPresidioAreas.includes(b);
                              
                              if (aIsSelected && !bIsSelected) return -1;
                              if (!aIsSelected && bIsSelected) return 1;
                            } else {
                              // If no search term, just prioritize selected items
                              const aIsSelected = selectedPresidioAreas.includes(a);
                              const bIsSelected = selectedPresidioAreas.includes(b);
                              
                              if (aIsSelected && !bIsSelected) return -1;
                              if (!aIsSelected && bIsSelected) return 1;
                            }
                            
                            return 0;
                          })
                          .map((area, index) => (
                          <div 
                            key={index} 
                            className={`flex items-center p-2 hover:bg-gray-100 cursor-pointer ${
                              selectedPresidioAreas.includes(area) ? 'bg-teal-50' : ''
                            }`}
                            data-area={area}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              
                              if (selectedPresidioAreas.includes(area)) {
                                const newSelectedAreas = selectedPresidioAreas.filter(a => a !== area);
                                setSelectedPresidioAreas(newSelectedAreas);
                                setFormData(prev => ({ ...prev, presidioAreas: newSelectedAreas }));
                              } else {
                                const newSelectedAreas = [...selectedPresidioAreas, area];
                                setSelectedPresidioAreas(newSelectedAreas);
                                setFormData(prev => ({ ...prev, presidioAreas: newSelectedAreas }));
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedPresidioAreas.includes(area)}
                              readOnly
                              className="mr-2 pointer-events-none"
                            />
                            <span className="text-sm">{area}</span>
                          </div>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-gray-500">No presidio areas available</div>
                      )}
                    </div>
                  </div>
                  {formErrors.presidioAreas && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.presidioAreas}</p>
                  )}
                </div>
                
                {/* Dependency Project */}
                <div>
                  <label htmlFor="dependencyProjectId" className="block text-sm font-medium text-gray-700 mb-1">
                    Dependent On Project (Optional)
                  </label>
                  <div className="relative">
                    {(() => {
                      // Filter available projects (exclude current project when editing)
                      const availableProjects = projects.filter((projectOption) => {
                        // When editing, exclude the current project to prevent self-dependency
                        if (project !== null && projectOption.projectId === project.projectId) {
                          return false;
                        }
                        return true;
                      });
                      
                      const isDisabled: boolean = projects.length === 0 || availableProjects.length === 0;
                      
                      return (
                        <select
                          id="dependsOnProjectId"
                          name="dependsOnProjectId"
                          value={formData.dependsOnProjectId}
                          onChange={handleChange}
                          disabled={isDisabled}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            isDisabled
                              ? 'bg-gray-100 cursor-not-allowed opacity-60'
                              : ''
                          }`}
                        >
                          <option value="">
                            {projects.length === 0 
                              ? 'No projects available' 
                              : availableProjects.length === 0
                              ? 'No other projects available'
                              : 'Select a project this depends on'}
                          </option>
                          {availableProjects.map((projectOption) => (
                            <option key={projectOption.projectId} value={projectOption.projectId}>
                              {projectOption.projectName}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex justify-end pt-4">
                  {project && onDelete && (
                    <button
                      type="button"
                      onClick={() => setIsDeleteConfirmOpen(true)}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md mr-auto hover:bg-red-700"
                    >
                      Delete
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCloseAttempt}
                    disabled={isSubmitting}
                    className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 text-sm font-medium text-white bg-[#0F2A4F] rounded-md flex items-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-blue-900'}`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                        {project ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      project ? 'Update Project' : 'Create Project'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
      />

             {/* Unsaved Changes Confirmation Modal */}
       <ConfirmationModal
         isOpen={isCloseConfirmOpen}
         onClose={() => setIsCloseConfirmOpen(false)}
         onConfirm={handleConfirmClose}
         title="Unsaved Changes"
         message="You have unsaved changes. Are you sure you want to leave this page? All your changes will be lost."
         confirmButtonText="Leave Page"
         cancelButtonText="Stay"
       />
    </div>
  );
};
